import { createOrganisationAPI } from "../api/apiFactory";
import type { DeactivateResult, IOrganisationAPI, IsOwnerResponse, ReactivateResult } from "../api/organisationApi";
import type { Organisation, OrganisationFilters, OrganisationSort } from "../types/Organisation";
import type { PaginatedResponse } from "../types/Pagination";

export class OrganisationService {
  public api: IOrganisationAPI;

  constructor(api?: IOrganisationAPI) {
    this.api = api || createOrganisationAPI();
  }

  private applyClientFilters(organisations: Organisation[], filters: OrganisationFilters): Organisation[] {
    return organisations.filter((organisation) => {
      if (filters.organisationId && organisation.id !== filters.organisationId) return false;

      if (filters.searchTerm) {
        const searchLower = filters.searchTerm.toLowerCase();
        const nameMatch = organisation.name.toLowerCase().includes(searchLower);
        if (!nameMatch) {
          return false;
        }
      }
      return true;
    });
  }

  private sortOrganisations(organisations: Organisation[], sort: OrganisationSort): Organisation[] {
    const sorted = [...organisations];

    sorted.sort((a, b) => {
      let comparison = 0;

      switch (sort.field) {
        case "name":
          comparison = a.name.localeCompare(b.name);
          break;
        case "active":
          comparison = Number(a.active) - Number(b.active);
          break;
      }

      return sort.order === "asc" ? comparison : -comparison;
    });

    return sorted;
  }

  async getOrganisations(filters?: OrganisationFilters, sort?: OrganisationSort): Promise<PaginatedResponse<Organisation>> {
    const response = await this.api.fetchSerializedOrganisations();
    let organisations = response.data;
    if (filters) {
      organisations = this.applyClientFilters(organisations, filters);
    }
    if (sort) {
      organisations = this.sortOrganisations(organisations, sort);
    }
    return {
      ...response,
      data: organisations,
    };
  }

  async getOrganisationById(id: string): Promise<Organisation | null> {
    return this.api.fetchOrganisationById(id);
  }

  async checkIsOwnerAndGetMembers(organisationId: string): Promise<IsOwnerResponse | null> {
    return this.api.checkIsOwnerAndGetMembers(organisationId);
  }

  async transferOwner(organisationId: string, newOwnerId: string): Promise<Organisation> {
    return this.api.transferOwner(organisationId, newOwnerId);
  }

  async setLocations(organisationId: string, permissableLocationIds: string[]): Promise<Organisation> {
    return this.api.setOrganisationLocations(organisationId, permissableLocationIds);
  }

  async getLocationBookingStatus(organisationId: string): Promise<{ lockedLocationIds: string[] }> {
    return this.api.getOrganisationLocationBookingStatus(organisationId);
  }

  async deactivate(id: string): Promise<DeactivateResult> {
    return this.api.deactivateOrganisation(id);
  }

  async reactivate(id: string): Promise<ReactivateResult> {
    return this.api.reactivateOrganisation(id);
  }

  async assignMember(organisationId: string, memberId: string): Promise<{ success: boolean }> {
    return this.api.assignMember(organisationId, memberId);
  }

  async renameOrganisation(organisationId: string, name: string): Promise<{ success: boolean }> {
    return this.api.renameOrganisation(organisationId, name);
  }
}
